import ArrowForward from "@mui/icons-material/ArrowForward";
import { Alert, AlertTitle, Box, Link, Stack, Typography } from "@mui/material";
import { IssuesDataGrid, IssueWithUrl } from "components/IssuesDataGrid";
import { CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION } from "encamp-shared/src/constants/tierii";
import { PresubmissionError } from "encamp-shared/src/presubmission/types";
import { ActivityType, GetTierIiReportQuery } from "generated-graphql/graphql";
import { ReactNode } from "react";

type Activity = NonNullable<
  GetTierIiReportQuery["tierIIReport"]["currentWorkflow"]
>["activities"][0];

type Facility = GetTierIiReportQuery["tierIIReport"]["facility"];
type Tenant = GetTierIiReportQuery["tierIIReport"]["tenant"];

type PresubmissionOutput = {
  issues?: IssueWithUrl[];
  validationBypassed?: boolean;
  bypassReason?: "state" | "facility";
  errors?: PresubmissionError[];
};

type ActivityErrorDisplayProps = {
  activity: Activity;
  facility?: Facility | null;
  tenant?: Tenant | null;
};

const facilityCredentialsUrlFor = (
  facility: Facility | null | undefined,
  tenant: Tenant | null | undefined
): string | null =>
  facility?.id != null && tenant?.id != null
    ? `/o/${tenant.id}/facilities/${facility.id}/credentials`
    : null;

const EPCRA_CREDENTIALS_PATH = "/staff/credentials/epcra";

// Deep-link to a specific credential implementation's drawer when we have its
// id; otherwise fall back to the management grid (e.g. when no implementation
// row exists yet). The grid does not hydrate a `?search=` param, so we don't
// pre-filter — staff land on the page and search there.
const epcraCredentialsUrlFor = (credentialImplementationId: string | undefined): string =>
  credentialImplementationId != null
    ? `${EPCRA_CREDENTIALS_PATH}/${credentialImplementationId}`
    : EPCRA_CREDENTIALS_PATH;

/**
 * Use this component to define a custom display for activity errors.
 *
 * For example, presubmission activities have issues as part of their
 * output so we have a custom display for those.
 */
export const ActivityErrorDisplay = ({ activity, facility, tenant }: ActivityErrorDisplayProps) => {
  const facilityCredentialsUrl = facilityCredentialsUrlFor(facility, tenant);

  if (activity.type === ActivityType.Presubmission) {
    const output = activity.output as PresubmissionOutput | undefined;
    const issues = (output?.issues ?? []) as IssueWithUrl[];
    const validationBypassed = output?.validationBypassed ?? false;
    const bypassReason = output?.bypassReason;
    const errors = output?.errors ?? [];

    return (
      <Stack sx={{ flex: 1 }} gap={1}>
        {validationBypassed && (
          <Alert severity="info">Validation was bypassed for this {bypassReason}.</Alert>
        )}
        {errors.length > 0 && (
          <Stack gap={1}>
            {errors.map((error, idx) => (
              <PresubmissionErrorAlert
                key={`${error.type}-${idx}`}
                error={error}
                facilityCredentialsUrl={facilityCredentialsUrl}
              />
            ))}
          </Stack>
        )}
        {issues.length > 0 && (
          <IssuesDataGrid issues={issues} validationBypassed={validationBypassed} />
        )}
      </Stack>
    );
  }

  if (
    activity.type === ActivityType.Error &&
    activity.description === CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION
  ) {
    return (
      <Box sx={{ flex: 1 }} component="section">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The submission workflow for this report cannot be created because the facility needs to
          have a single Tier II credential.
        </Typography>
        <Stack component="ol" sx={{ pl: 2.5, m: 0 }} spacing={1}>
          <Typography component="li" variant="body2">
            {facilityCredentialsUrl != null ? (
              <Link href={facilityCredentialsUrl} target="_blank" rel="noopener noreferrer">
                Update the Tier II credential(s) for this facility
              </Link>
            ) : null}
          </Typography>
          <Typography component="li" variant="body2">
            After ensuring the facility has a single Tier II credential, use{" "}
            <strong>Reset Report</strong> in the left sidebar and force cancel the workflow so that
            a new workflow can be created.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return <></>;
};

type PresubmissionErrorAlertProps = {
  error: PresubmissionError;
  facilityCredentialsUrl: string | null;
};

const ArrowLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}
  >
    {children}
    <ArrowForward sx={{ fontSize: 16 }} />
  </Link>
);

// Deep-links to the implementation's drawer when we have its id, else the
// management grid. Used for the cases whose fix lives in the implementation
// (advance status, etc.).
const ManageLink = ({
  credentialImplementationId,
}: {
  credentialImplementationId: string | undefined;
}) => (
  <ArrowLink href={epcraCredentialsUrlFor(credentialImplementationId)}>
    {credentialImplementationId != null
      ? "Open the credential implementation"
      : "Open EPCRA credential management"}
  </ArrowLink>
);

// Shared layout for the credential presubmission errors: a concise message
// line, the next action(s) (numbered when there's more than one), and an
// optional trailing affordance. Errors whose fix is the on-page "Verify
// Credential" button omit `action` (no navigating away); errors fixed in the
// implementation drawer pass the deep-link as `action`.
const CredentialErrorAlert = ({
  title,
  message,
  steps,
  action,
}: {
  title: string;
  message: string;
  steps: ReactNode[];
  action?: ReactNode;
}) => (
  <Alert severity="warning" variant="outlined">
    <AlertTitle>{title}</AlertTitle>
    <Typography variant="body2" sx={{ mb: steps.length > 0 ? 1 : 0 }}>
      {message}
    </Typography>
    {steps.length === 1 && <Typography variant="body2">{steps[0]}</Typography>}
    {steps.length > 1 && (
      <Stack component="ol" sx={{ pl: 2.5, m: 0 }} spacing={0.5}>
        {steps.map((step, idx) => (
          <Typography component="li" variant="body2" key={idx}>
            {step}
          </Typography>
        ))}
      </Stack>
    )}
    {action != null && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Alert>
);

const verifyCredentialStep = (
  <>
    Click <strong>Verify Credential</strong> on this page.
  </>
);

const PresubmissionErrorAlert = ({
  error,
  facilityCredentialsUrl,
}: PresubmissionErrorAlertProps) => {
  switch (error.type) {
    case "credential_missing_facility":
      return (
        <CredentialErrorAlert
          title="Credential is missing this facility in the portal"
          message={error.message}
          steps={["Add this facility to the portal account.", verifyCredentialStep]}
        />
      );
    case "credential_manifest_unverified":
      return (
        <CredentialErrorAlert
          title="Credential hasn't been verified yet"
          message={error.message}
          steps={[verifyCredentialStep]}
        />
      );
    case "credential_implementation_missing":
      return (
        <CredentialErrorAlert
          title="Credential implementation is missing"
          message={error.message}
          steps={[
            "This is unexpected — credential implementations are created automatically. Report it to engineering so we can investigate why this one is missing.",
          ]}
        />
      );
    case "credential_implementation_incomplete":
      return (
        <CredentialErrorAlert
          title="Credential implementation isn't complete"
          message={error.message}
          steps={["Advance the implementation status to COMPLETE."]}
          action={
            <ManageLink credentialImplementationId={error.details.credentialImplementationId} />
          }
        />
      );
    case "credential_not_found":
      return (
        <CredentialErrorAlert
          title="No credential is linked to this facility"
          message={error.message}
          steps={["Link a Tier II credential to this facility."]}
          action={
            facilityCredentialsUrl != null ? (
              <ArrowLink href={facilityCredentialsUrl}>Link a credential</ArrowLink>
            ) : undefined
          }
        />
      );
    case "credential_verification_failed":
      return (
        <CredentialErrorAlert
          title="Credential failed verification"
          message={error.message}
          steps={["Update the saved username and password.", verifyCredentialStep]}
        />
      );
    case "portal_inactive":
      return (
        <CredentialErrorAlert
          title="Portal is inactive"
          message={error.message}
          steps={[
            <>
              Once the portal is active again, click <strong>Verify Credential</strong> on this
              page.
            </>,
          ]}
        />
      );
    default:
      return (
        <Alert severity="error">
          <Typography variant="body2">{error.message}</Typography>
        </Alert>
      );
  }
};
